import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined"
import SendRoundedIcon from "@mui/icons-material/SendRounded"
import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import InputBase from "@mui/material/InputBase"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useRef, useState } from "react"

import Row from "../../../common/components/Row"
import DialogHeader from "../components/DialogHeader"
import SelectCampaignAutoComplete, {
  useSelectCampaignAutoCompleteState,
} from "../components/SelectCampaignAutoComplete"
import { useDialogContainer } from "../utils"

function templateText(campaign, candidateId) {
  if (!campaign) return ""
  if (campaign.rendered_text) {
    return campaign.rendered_text
  }
  return campaign.text || ""
}

export default function SmsInboxReplyComposer({
  thread,
  mailboxConnected,
  isReplying,
  templatesRevision = 0,
  onManageTemplates,
  onSend,
}) {
  const Container = useDialogContainer()
  const candidateId = thread?.candidate_id
  const params = new URLSearchParams()
  if (candidateId) params.set("candidate_id", String(candidateId))
  if (templatesRevision) params.set("r", String(templatesRevision))
  const query = params.toString()
  const campaignsAPIURL = `/crm/api/concierge-sms/inbox/templates/${
    query ? `?${query}` : ""
  }`
  const {
    availableCampaigns,
    setAvailableCampaigns,
    selectedCampaign,
    setSelectedCampaign,
    campaignInputValue,
    setCampaignInputValue,
    campaignsLoaded,
    campaignsLoading,
  } = useSelectCampaignAutoCompleteState(Boolean(thread), campaignsAPIURL)
  const [expanded, setExpanded] = useState(false)
  const [draftText, setDraftText] = useState("")
  const appliedTemplateId = useRef(null)

  useEffect(() => {
    if (!expanded) return
    if (
      selectedCampaign?.id &&
      appliedTemplateId.current !== selectedCampaign.id
    ) {
      appliedTemplateId.current = selectedCampaign.id
      setDraftText(templateText(selectedCampaign, candidateId))
    }
  }, [expanded, selectedCampaign?.id, candidateId])

  function handleExpand() {
    if (!mailboxConnected) return
    setExpanded(true)
  }

  function handleCloseComposer() {
    setExpanded(false)
  }

  function handleSend() {
    const message = draftText.trim()
    if (!message) return
    Promise.resolve(onSend(message, selectedCampaign?.id)).then((sent) => {
      if (sent === false) return
      appliedTemplateId.current = null
      setSelectedCampaign(null)
      setCampaignInputValue("")
      setDraftText("")
      setExpanded(false)
    })
  }

  const canSend = mailboxConnected && Boolean(draftText.trim())
  const preview = draftText.trim()

  return (
    <>
      <Row
        component="button"
        type="button"
        onClick={handleExpand}
        disabled={!mailboxConnected}
        aria-expanded={expanded}
        aria-label="Write a reply"
        sx={{
          appearance: "none",
          WebkitAppearance: "none",
          width: "100%",
          alignItems: "center",
          gap: 1.25,
          px: 1.75,
          py: 1.25,
          m: 0,
          color: "inherit",
          border: "1px solid",
          borderColor: "colors.gray200",
          borderRadius: 2,
          bgcolor: mailboxConnected ? "background.paper" : "colors.gray50",
          cursor: mailboxConnected ? "text" : "not-allowed",
          textAlign: "left",
          font: "inherit",
          "&:hover": mailboxConnected
            ? { borderColor: "colors.gray300", bgcolor: "colors.gray50" }
            : undefined,
        }}
      >
        <ReplyOutlinedIcon
          sx={{ fontSize: 20, color: "colors.gray500", flexShrink: 0 }}
        />
        <InputBase
          readOnly
          fullWidth
          value={preview}
          placeholder={
            mailboxConnected
              ? preview
                ? "Continue reply…"
                : "Write a reply…"
              : "Buy a calling number to reply"
          }
          sx={{
            pointerEvents: "none",
            "& input": {
              cursor: mailboxConnected ? "text" : "not-allowed",
              p: 0,
              fontSize: 14,
              color: preview ? "text.primary" : "colors.gray500",
            },
          }}
        />
      </Row>
      <Container
        open={expanded}
        onClose={handleCloseComposer}
        anchor="right"
        PaperProps={{
          sx: { width: { xs: "100%", sm: 480 }, maxWidth: "100%" },
        }}
      >
        <DialogHeader
          title="Reply"
          onClose={handleCloseComposer}
          actions={
            <Button
              size="small"
              variant="outlined"
              onClick={onManageTemplates}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Manage templates
            </Button>
          }
        />
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            pt: 2.5,
          }}
        >
          <Typography variant="body2" sx={{ color: "colors.gray600" }}>
            {(thread?.candidate_name || thread?.contact_name) &&
            thread?.counterparty_phone
              ? `Replying to ${
                  thread.candidate_name || thread.contact_name
                } · ${thread.counterparty_phone}`
              : thread?.counterparty_phone
              ? `Replying to ${thread.counterparty_phone}`
              : "Write a text for this conversation."}
          </Typography>
          <SelectCampaignAutoComplete
            compact
            hideHelperText
            size="small"
            availableCampaigns={availableCampaigns}
            setAvailableCampaigns={setAvailableCampaigns}
            campaignInputValue={campaignInputValue}
            setCampaignInputValue={setCampaignInputValue}
            selectedCampaign={selectedCampaign}
            setSelectedCampaign={setSelectedCampaign}
            campaignsLoaded={campaignsLoaded}
            campaignsLoading={campaignsLoading}
            createLinkLabel="Manage templates"
            groupByScope
            loadingLabel="Loading SMS templates..."
            onCreateCampaignLinkClicked={onManageTemplates}
          />
          {selectedCampaign ? (
            <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="caption" sx={{ color: "colors.gray600" }}>
                {candidateId
                  ? "Placeholders are filled for this candidate. Edits apply to this reply only."
                  : "This thread is not linked to a candidate, so placeholders will not be filled."}
              </Typography>
              {selectedCampaign.is_global ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label="Default template"
                />
              ) : null}
            </Row>
          ) : null}
          <TextField
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder="Type a text message…"
            multiline
            minRows={8}
            maxRows={16}
            disabled={!mailboxConnected || isReplying}
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <LoadingButton
            variant="contained"
            startIcon={<SendRoundedIcon />}
            loading={isReplying}
            disabled={!canSend}
            onClick={handleSend}
            sx={{ textTransform: "none", fontWeight: 600, px: 2.5 }}
          >
            Send SMS
          </LoadingButton>
        </DialogActions>
      </Container>
    </>
  )
}
